/**
 * SkillBadge.tsx
 * The skill-fit badge for the "Staff this job" v2 view (STAFF-VIEW-V2.md). Given
 * a person's level (1–5 | null) for a task's required skill, it reads at a glance
 * whether they're a fit:
 *   - Qualified · L4   (L3+)            — green, a check.
 *   - Needs mentor · L2 (L1/L2)         — amber, a school cap.
 *   - Not qualified    (skill req, no level) — red, a warning.
 *   - No skill needed  (task needs none)     — neutral grey, no fuss.
 *
 * Colors from theme.schedule.skill* tokens — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { classifySkill, skillBadgeLabel, skillBadgeCompactLabel, type SkillFit } from './staffSkillModel';

export interface SkillBadgeProps {
  level: number | null | undefined;
  skillRequired: boolean;
  /** Compact = icon + short label; used on dense picker rows. */
  size?: 'sm' | 'md';
  /**
   * 'full'    — worded label ("Qualified · L4"): legend, picker rows.
   * 'compact' — WORDLESS (just "L4" + colored icon): the person cards on task
   *             cards (BUG-2026-0141 #5). Color + icon + legend carry the meaning.
   */
  variant?: 'full' | 'compact';
}

const ICON_BY_FIT: Record<SkillFit, React.ElementType> = {
  qualified: CheckCircleRoundedIcon,
  'needs-mentor': SchoolRoundedIcon,
  unrated: WarningRoundedIcon,
  'no-skill': RemoveRoundedIcon,
};

export const SkillBadge: React.FC<SkillBadgeProps> = ({ level, skillRequired, size = 'md', variant = 'full' }) => {
  const theme = useTheme();
  const fit = classifySkill(level, skillRequired);
  const compact = variant === 'compact';

  const color =
    fit === 'qualified' ? theme.schedule.skillQualified
      : fit === 'needs-mentor' ? theme.schedule.skillMentor
        : fit === 'unrated' ? theme.schedule.skillUnqualified
          : theme.palette.text.secondary;

  const label = compact ? skillBadgeCompactLabel(fit, level) : skillBadgeLabel(fit, level);
  const Icon = ICON_BY_FIT[fit];
  const neutral = fit === 'no-skill';
  const fontSize = compact ? '0.62rem' : size === 'sm' ? '0.6rem' : '0.66rem';
  const iconSize = compact ? 12 : size === 'sm' ? 11 : 13;

  const tip =
    fit === 'qualified' ? 'Qualified — can work this task alone (L3+).'
      : fit === 'needs-mentor' ? 'Junior (L1/L2) — needs an L3+ mentor on the task.'
        : fit === 'unrated' ? 'No recorded skill level for this task — not qualified.'
          : 'This task requires no specific skill.';

  return (
    <Tooltip title={tip} placement="top" arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          px: 0.6,
          py: 0.15,
          borderRadius: '999px',
          border: `1px solid ${neutral ? theme.border.color : alpha(color, 0.5)}`,
          backgroundColor: neutral ? 'transparent' : alpha(color, 0.12),
          color,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon sx={{ fontSize: iconSize }} />
        <Typography component="span" sx={{ fontSize, fontWeight: 700, color }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};
